import { Button } from '@99designs/common/components/Button';
import { Icon } from '@99designs/common/components/Icon';
import { getComponentClasses } from '@99designs/common/utils/app/index';
import { SmartShootVideoPackage } from '__generated__/types';
import { updatePhotographyBrief_updateStructuredBrief_fields_SmartShootPhotographyFields as PhotographyBriefFields } from '__generated__/updatePhotographyBrief';
import { updateVideoBrief_updateStructuredBrief_fields_SmartShootVideoFields as VideoBriefFields } from '__generated__/updateVideoBrief';
import classNames from 'classnames';
import { handlePromise } from 'common/utils/Bugsnag';
import { ObservableForm, Observe } from 'projects/components/ObservableForm';
import { Color } from 'projects/consts';
import React from 'react';
import BriefStep from './BriefStep';

interface Props<F> {
    form: ObservableForm<F>;
    step: number;
    errorMessage?: string;
    briefType: 'video' | 'photo';
}

const consultationUrl =
    'https://meetings.hubspot.com/melanie-panergo1/video-consultation-99designs';

export default class PackageForm<
    F extends VideoBriefFields | PhotographyBriefFields
> extends React.Component<Props<F>> {
    render() {
        let briefSubHeading;
        const tableVariations = ['full-width', 'products', 'offset-top'];
        if (this.props.briefType === 'video') {
            briefSubHeading = (
                <>
                    Select a video package that fits your project needs and budget. Need help
                    deciding which package works for you?{' '}
                    <a className="link" href={consultationUrl} target="_blank">
                        Book a consultation with one of our video experts.
                    </a>
                </>
            );
            tableVariations.push('smartshoot-custom-pricing');
        } else {
            briefSubHeading =
                'Select a photography package that fits your project needs and budget.';
        }

        return (
            <BriefStep
                step={this.props.step}
                heading="Your package"
                subheading={briefSubHeading}
                variations={['single-col']}
                hideImage
                required
            >
                {this.props.errorMessage && (
                    <p
                        data-name="package"
                        className="paragraph"
                        style={
                            this.props.errorMessage
                                ? { color: Color.risingSun, textAlign: 'center' }
                                : {}
                        }
                    >
                        {this.props.errorMessage}
                    </p>
                )}
                <div data-scrollable>
                    <div data-scrollable-inner>
                        <Table variations={tableVariations}>
                            <TableHead>
                                <TableRow>
                                    <TableCell type={'th'} />
                                    {packages(this.props.briefType).map(function(
                                        pricePackage,
                                        index
                                    ) {
                                        return (
                                            <TableCell
                                                type={'th'}
                                                variations={pricePackage.tableCellVariations}
                                                key={index}
                                            >
                                                <div
                                                    className={classNames(
                                                        getComponentClasses(
                                                            'bordered-box',
                                                            pricePackage.borderedBoxVariations
                                                        )
                                                    )}
                                                >
                                                    <div className="heading heading--h5 heading--no-margin heading--punt-road">
                                                        {pricePackage.package.type}
                                                    </div>
                                                </div>

                                                <div className="heading heading--h5 heading--quarter-margin">
                                                    {pricePackage.package.title}
                                                </div>

                                                <div className="paragraph paragraph--no-margin">
                                                    {pricePackage.package.description}
                                                </div>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {packageFeatures(this.props.briefType).map(
                                    (packageFeature, index) => {
                                        return (
                                            <TableRow key={index}>
                                                <TableCell
                                                    variations={[
                                                        'left-aligned',
                                                        'no-horizontal-padding',
                                                    ]}
                                                >
                                                    {packageFeature.title}
                                                </TableCell>
                                                {this.renderFeatureValueTableCell(
                                                    packageFeature,
                                                    0
                                                )}
                                                {this.renderFeatureValueTableCell(
                                                    packageFeature,
                                                    1
                                                )}
                                                {packageFeature.values.length === 3 &&
                                                    this.renderFeatureValueTableCell(
                                                        packageFeature,
                                                        2
                                                    )}
                                            </TableRow>
                                        );
                                    }
                                )}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TableCell />
                                    <TableCell
                                        variations={['no-horizontal-padding', 'space-right']}
                                    >
                                        <Observe
                                            form={this.props.form}
                                            name="package"
                                            render={() =>
                                                this.renderPackageField(
                                                    SmartShootVideoPackage.BASIC
                                                )
                                            }
                                        />
                                    </TableCell>
                                    <TableCell variations={['no-horizontal-padding', 'space-left']}>
                                        <Observe
                                            form={this.props.form}
                                            name="package"
                                            render={() =>
                                                this.renderPackageField(
                                                    SmartShootVideoPackage.PREMIUM
                                                )
                                            }
                                        />
                                    </TableCell>
                                    {this.props.briefType === 'video' &&
                                        this.renderCustomPackageButton()}
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </div>
                </div>
                <p className="paragraph paragraph--root paragraph--soft paragraph--offset-right paragraph--right-aligned paragraph--small">
                    For all legal mumbo jumbo please read the{' '}
                    <a
                        className="link link--default link--soft"
                        href="/legal/smartshoot-terms-of-use"
                        target="_blank"
                    >
                        Terms of Service
                    </a>
                </p>
            </BriefStep>
        );
    }

    renderFeatureValueTableCell(packageFeature: PackageFeature, index: number) {
        let rowSpan = 1;
        const variations = ['karl-the-fog'];

        if (packageFeature.rowSpans && packageFeature.rowSpans.length === index + 1) {
            rowSpan = packageFeature.rowSpans[index];
        }

        if (index > 0) {
            variations.push('space-left');
        }

        return (
            <TableCell variations={variations} rowSpan={rowSpan}>
                {rowSpan === 1 && (
                    <div className="table-cell__small-only">{packageFeature.title}</div>
                )}
                {renderFeatureValue(packageFeature.values[index])}
            </TableCell>
        );
    }

    renderPackageField(packageType: SmartShootVideoPackage) {
        if (this.props.form.get('package') === packageType) {
            return this.renderActiveButton();
        }

        return this.renderInactiveButton(packageType);
    }

    renderActiveButton() {
        return (
            <div style={{ color: Color.forrestLyf }}>
                <Button variations={['chameleon', 'fluid', 'jumbo', 'primary']}>
                    <Icon type={'tick-3'} variations={['offset-right']} /> Selected
                </Button>
            </div>
        );
    }

    renderInactiveButton(packageType: SmartShootVideoPackage) {
        return (
            <Button
                onClick={() => this.handlePackageSelection(packageType)}
                variations={['jumbo', 'marketing-secondary', 'punt-road', 'fluid']}
            >
                Select package
            </Button>
        );
    }

    renderCustomPackageButton() {
        return (
            <TableCell variations={['no-horizontal-padding', 'space-left', 'heritage']}>
                <Button
                    href={consultationUrl}
                    target="_blank"
                    variations={['jumbo', 'marketing-secondary', 'chameleon', 'fluid']}
                >
                    Get a custom quote
                </Button>
            </TableCell>
        );
    }

    handlePackageSelection(packageType: SmartShootVideoPackage) {
        handlePromise(this.props.form.set('package', packageType), 'VideoBrief Package');
    }
}

interface TableProps {
    variations?: string[];
    type?: string;
}

interface TableCellProps extends TableProps {
    rowSpan?: number;
}

const Table: React.FunctionComponent<TableProps> = props => (
    <table
        className={classNames(getComponentClasses('table', props.variations))}
        cellPadding={0}
        cellSpacing={0}
    >
        {props.children}
    </table>
);

const TableHead: React.FunctionComponent<TableProps> = props => <thead>{props.children}</thead>;

const TableBody: React.FunctionComponent<TableProps> = props => <tbody>{props.children}</tbody>;

const TableFooter: React.FunctionComponent<TableProps> = props => <tfoot>{props.children}</tfoot>;

const TableRow: React.FunctionComponent<TableProps> = props => <tr>{props.children}</tr>;

const TableCell: React.FunctionComponent<TableCellProps> = props => {
    const tag = props.type ? props.type : 'td';
    const content = !props.children ? (
        ''
    ) : (
        <div className={classNames(getComponentClasses('table-cell', props.variations))}>
            <div>{props.children}</div>
        </div>
    );

    if (props.rowSpan) {
        return React.createElement(tag, { rowSpan: props.rowSpan }, content);
    } else {
        return React.createElement(tag, {}, content);
    }
};

function renderFeatureValue(featureValue: string | number | boolean) {
    if (typeof featureValue === 'boolean') {
        const icon = featureValue ? 'tick-1' : 'minus-2';
        const variations = featureValue ? ['large'] : ['large', 'semi-transparent'];

        return <Icon variations={variations} type={icon} />;
    }

    return <div className="paragraph paragraph--no-margin">{featureValue}</div>;
}

function packages(briefType: 'video' | 'photo') {
    if (briefType === 'video') {
        return videoBriefPackages();
    } else {
        return photoBriefPackages();
    }
}

function videoBriefPackages() {
    return [
        {
            package: {
                type: 'Basic',
                title: 'US$499',
                description: 'Best suited to custom social media advertisements and campaigns.',
            },
            borderedBoxVariations: ['square-small', 'centered', 'in-text-flow', 'pebble-beach'],
            tableCellVariations: ['for-offset-top', 'karl-the-fog'],
        },
        {
            package: {
                type: 'Premium',
                title: 'US$999',
                description:
                    'Great for custom business, branding or product marketing video content.',
            },
            borderedBoxVariations: ['square-small', 'centered', 'in-text-flow', 'punt-road'],
            tableCellVariations: ['for-offset-top', 'karl-the-fog', 'space-left'],
        },
        {
            package: {
                type: 'Custom',
                title: 'Get a quote',
                description: 'Create your own package to suit your needs.',
            },
            borderedBoxVariations: ['square-small', 'centered', 'in-text-flow', 'heritage'],
            tableCellVariations: ['for-offset-top', 'karl-the-fog', 'space-left'],
        },
    ];
}

function photoBriefPackages() {
    return [
        {
            package: {
                type: 'Basic',
                title: 'US$299',
                description: 'Great for quick and simple shoots with limited editing.',
            },
            borderedBoxVariations: ['square-small', 'centered', 'in-text-flow', 'pebble-beach'],
            tableCellVariations: ['for-offset-top', 'karl-the-fog'],
        },
        {
            package: {
                type: 'Premium',
                title: 'US$699',
                description: 'Best for those who need more images and professional editing.',
            },
            borderedBoxVariations: ['square-small', 'centered', 'in-text-flow', 'punt-road'],
            tableCellVariations: ['for-offset-top', 'karl-the-fog', 'space-left'],
        },
    ];
}

function packageFeatures(briefType: 'video' | 'photo') {
    if (briefType === 'video') {
        return videoPackageFeatures();
    } else {
        return photoPackageFeatures();
    }
}

interface PackageFeature {
    title: string;
    values: Array<string | boolean | number>;
    rowSpans?: Array<number>;
}

function videoPackageFeatures(): PackageFeature[] {
    return [
        { title: '100% money back guarantee', values: [true, true, false] },
        {
            title: 'Video length',
            values: [
                'Up to 30 seconds',
                'Up to 60 seconds + a shorter 15-second version',
                "Looking for something else? Whether it's more shoot time or a different type of a video , book a consultation with one of our video experts to get a custom quote",
            ],
            rowSpans: [1, 1, 7],
        },
        { title: 'Shoot duration', values: ['Up to 60 minutes', 'Up to 90 minutes'] },
        { title: 'Rounds of editing', values: [1, 2] },
        {
            title: 'Graphic overlay',
            values: [
                'Messaging in lower screen, call-to-action.',
                'Messaging in lower screen, call-to-action, titles and your logo.',
            ],
        },
        { title: 'Music (royalty-free)', values: [true, true] },
        { title: 'Script created by filmmaker', values: [true, true] },
        { title: 'Voice-over talent', values: [false, true] },
    ];
}

function photoPackageFeatures(): PackageFeature[] {
    return [
        {
            title: 'Number of photos',
            values: ['8 edited photos', '15 edited photos'],
        },
        { title: 'Single location', values: [true, true] },
        { title: 'Color correction', values: [true, true] },
        { title: 'Exposure correction', values: [true, true] },
        { title: 'Perspective correction', values: [false, true] },
        { title: 'Cropping', values: [false, true] },
        { title: 'Blemish removal', values: [false, true] },
    ];
}
